<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { VolunteerQualificationPayload } from '@/types/volunteer'
import { useVolunteerSignUp } from '@/composables/useVolunteerSignUp'
import VolunteerWizardHeader from '@/components/volunteer/VolunteerWizardHeader.vue'
import VolunteerStepDetails from '@/components/volunteer/VolunteerStepDetails.vue'
import VolunteerStepAvailability from '@/components/volunteer/VolunteerStepAvailability.vue'
import VolunteerStepReview from '@/components/volunteer/VolunteerStepReview.vue'
import RegistrationProgressBar from '@/components/registration/RegistrationProgressBar.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const orgSlug = String(route.params['orgSlug'])
const eventSlug = String(route.params['eventSlug'])

const { info, loading, errors, fullSlotIds, fullRoleIds, fetchInfo, submit } =
  useVolunteerSignUp()

const currentStep = ref(0)

const formData = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
})

const selectedSlotIds = ref<number[]>([])
const selectedRoleIds = ref<number[]>([])
const qualificationAnswers = ref<VolunteerQualificationPayload[]>([])

const stepLabels = computed(() => [
  t('volunteer.wizard.steps.you'),
  t('volunteer.wizard.steps.when'),
  t('volunteer.wizard.steps.confirm'),
])

function goNext(): void {
  currentStep.value++
}

function goBack(): void {
  currentStep.value--
}

/** When 409 is received, go back to step 2 to show the conflicts */
function goToStep2(): void {
  currentStep.value = 1
}

async function handleSubmit(): Promise<void> {
  const payload = {
    first_name: formData.value.first_name,
    last_name: formData.value.last_name,
    email: formData.value.email,
    phone: formData.value.phone,
    emergency_contact_name: formData.value.emergency_contact_name || null,
    emergency_contact_phone: formData.value.emergency_contact_phone || null,
    slot_ids: selectedSlotIds.value,
    role_ids: selectedRoleIds.value,
    qualifications: qualificationAnswers.value.length > 0 ? qualificationAnswers.value : undefined,
  }

  const result = await submit(eventSlug, payload)

  if (result) {
    await router.push({
      name: 'public.volunteer.confirmation',
      params: {
        orgSlug,
        eventSlug,
        confirmationToken: result.confirmation_token,
      },
    })
    return
  }

  // 409 conflict — go back to step 2
  if (fullSlotIds.value.length > 0 || fullRoleIds.value.length > 0) {
    goToStep2()
  }
}

onMounted(async () => {
  await fetchInfo(eventSlug)
  if (!info.value) {
    // volunteers disabled or event not found — redirect to event page
    await router.replace({ name: 'public.event', params: { orgSlug, eventSlug } })
  }
})
</script>

<template>
  <div class="min-h-screen bg-slate-50 flex flex-col">
    <!-- Loading -->
    <div v-if="loading && !info" class="flex items-center justify-center min-h-screen">
      <svg class="animate-spin h-10 w-10 text-sky-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <template v-else-if="info">
      <!-- Header -->
      <VolunteerWizardHeader
        :event-name="info.event.name"
        :organisation-name="info.event.organisation.name"
      />

      <!-- Wizard body -->
      <div class="max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 flex-1">
        <!-- Progress bar -->
        <div class="mb-8">
          <RegistrationProgressBar
            :step-labels="stepLabels"
            :current-step="currentStep"
            variant="volunteer"
          />
        </div>

        <!-- Step 1 — Your details -->
        <VolunteerStepDetails
          v-if="currentStep === 0"
          :event-slug="eventSlug"
          :info="info"
          :form-data="formData"
          :errors="errors"
          @update:form-data="formData = $event"
          @next-clicked="goNext"
        />

        <!-- Step 2 — Availability -->
        <VolunteerStepAvailability
          v-else-if="currentStep === 1"
          :slots="info.slots"
          :roles="info.roles"
          :selected-slot-ids="selectedSlotIds"
          :selected-role-ids="selectedRoleIds"
          :full-slot-ids="fullSlotIds"
          :full-role-ids="fullRoleIds"
          :errors="errors"
          @update:selected-slot-ids="selectedSlotIds = $event"
          @update:selected-role-ids="selectedRoleIds = $event"
          @next-clicked="goNext"
          @back-clicked="goBack"
        />

        <!-- Step 3 — Review & submit -->
        <VolunteerStepReview
          v-else-if="currentStep === 2"
          :info="info"
          :form-data="formData"
          :selected-slot-ids="selectedSlotIds"
          :selected-role-ids="selectedRoleIds"
          :qualification-answers="qualificationAnswers"
          :loading="loading"
          :errors="errors"
          @update:qualification-answers="qualificationAnswers = $event"
          @submit-clicked="handleSubmit"
          @back-clicked="goBack"
        />
      </div>

      <!-- Footer -->
      <AppFooter />
    </template>
  </div>
</template>
